import React, { useEffect, useState } from "react";
import { Avatar, Button, Flex, Rate, Image } from "antd";
import ProductMap from "@components/ProductMap/ProductMap";
import ProductProperties from "@components/ProductProperties/ProductProperties";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";
import ImagesCarousel from "@components/ImagesCarousel/ImagesCarousel";
import CustomerReviews from "@components/CustomerReviews/CustomerReviews";
import ProductControl from "@components/ProductControl/ProductControl";
import { ProductApi } from "services/ProductApi";
import { StarFilled } from "@ant-design/icons";
import messageIcon from "@assets/icons/contact.svg";
import Helpers from "@utils/Helpers";
import "./ProductDetail.scss";
import { errorLog } from "logging";
import { IProductForm } from "@utils/Interfaces";
import { useSearchParams } from "react-router-dom";
import { useUser } from "contexts/UserContext";
import { Link } from "react-router-dom";
import { text } from "stream/consumers";

const ProductDetail: React.FC = () => {
  const [urlParams, setUrlParams] = useSearchParams();
  const { userProfile } = useUser();
  const { setIsOpenChatbox, setSelectedReceiver, selectedReceiver } = useUser();
  const [product, setProduct] = useState<any>(null);
  const [productNearBy, setProductNearBy] = useState(null);
  const [distance, setDistance] = useState(null);
  const [deliveryTime, setDeliveryTime] = useState(null);
  const [productForm] = useState<IProductForm | null>(null);
  const [shippingCost] = useState(productForm?.shipping_cost || 4.35);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await ProductApi.GetProductDetail(
          parseInt(urlParams.get("productId")!)
        );
        if (response.status === 200) {
          setProduct(response.data.data);
          if (
            response.data.data.lat &&
            response.data.data.lat !== 0 &&
            response.data.data.lon &&
            response.data.data.lon !== 0
          ) {
            await fetchProductNearBy(
              response.data.data.lat,
              response.data.data.lon
            );
          }
        }
      } catch (error) {
        Helpers.notification.error("Can't fetch product. Please try again");
        errorLog("ProductDetail.tsx - fetchProduct", error);
      }
    };
    if (urlParams.get("productId")) {
      fetchProduct();
    }
  }, [urlParams.get("productId")]);

  const fetchProductNearBy = async (lat: number, lon: number) => {
    try {
      const response = await ProductApi.NearBy(lat, lon);
      if (response.status === 200) {
        setProductNearBy(response.data);
      }
    } catch (error) {
      Helpers.notification.error("Can't fetch product. Please try again");
      errorLog("ProductDetail.tsx - fetchProductNearBy", error);
    }
  };

  return (
    <div className="ProductDetail">
      <div className="container">
        <div className="product-detail-wrap">
          <div className={`bidding-control-wrap`}>
            <div className="product-title">
              <div className="product-name-rate">
                <div className="product-name">{product?.hay_type}</div>
                <div className="product-rating">
                  <Rate disabled defaultValue={0} />
                  <span className="ms-1">0 reviews</span>
                </div>
              </div>
              <div className="product-id">ID: #{product?.product_id}</div>
            </div>
            <ProductControl
              sellingMethod={product?.selling_method}
              data={product}
              distance={distance}
              deliveryTime={deliveryTime}
              shippingCost={shippingCost}
            />
            <div className="producer">
              <Flex gap={12} align="middle">
                {product?.farm_details?.farm_avatar ? (
                  <Avatar src={product?.farm_details?.farm_avatar} size={70} />
                ) : (
                  <Avatar size={70} className="user-avatar">
                    {product?.farm_details?.display_name[0]}
                  </Avatar>
                )}
                <div className="producer-info">
                  <div className="farm-name">
                    <span className="farm-name-text">
                      {product?.farm_details?.display_name || " "}
                    </span>{" "}
                    <StarFilled className="star-rating" /> 4.5
                  </div>
                  <div className="farm-location">
                    <FontAwesomeIcon
                      className="me-2 small location-icon"
                      icon={faLocationDot}
                    />
                    {product?.farm_details?.farm_province}
                  </div>
                  <Link
                    to={`/producer/detail?producerId=${product?.farm_details?.producer_id}`}
                  >
                    <div className="form-view-action">View Farm Profile</div>
                  </Link>
                </div>
                {product?.farm_details?.producer_id !==
                  userProfile?.user_id && (
                  <div className="producer-contact">
                    <Button
                      className="contact-btn"
                      onClick={() => {
                        setIsOpenChatbox(true);
                        setSelectedReceiver({
                          ...selectedReceiver,
                          receiverId: product?.farm_details?.producer_id,
                          receiverName: product?.farm_details?.display_name,
                          receiverAvatar: product?.farm_details?.farm_avatar,
                          referenceProduct: {
                            productImage: product?.images[0],
                            productType: product?.hay_type,
                            productId: product?.product_id,
                            quantity: product?.quantity,
                            weightPerBale: product?.bale_weight,
                            pricePerUnit:
                              product.unit == "Ton"
                                ? product?.price_per_tons
                                : product.price_per_bale,
                          },
                        });
                      }}
                    >
                      <img
                        src={messageIcon}
                        alt="contact"
                        width={20}
                        className="contact-icon"
                      />
                      Contact Producer
                    </Button>
                  </div>
                )}
              </Flex>
            </div>
          </div>
          <div className={`properties-wrap`}>
            <ProductMap
              productNearBy={productNearBy}
              mainId={product?.product_id}
              setDistance={setDistance}
              setDeliveryTime={setDeliveryTime}
              isProductDetail={true}
              setUrlParams={setUrlParams}
            />

            <ProductProperties
              data={product}
              deliveryTime={deliveryTime}
              distance={distance}
              type="Product Detail"
            />
          </div>
        </div>
        <ImagesCarousel imageList={product?.images} />
        <Image
          className="mt-5"
          src={Helpers.getImageFrontPage("sale")}
          preview={false}
        />
        <CustomerReviews
          productId={urlParams.get("productId") ? parseInt(urlParams.get("productId")!, 10) : 0}
        />
      </div>
    </div>
  );
};

export default ProductDetail;
